import {
    ANALOG_INPUT_BINDING_IDS,
    isSameDigitalInput,
    normalizeKey,
    type DigitalInput,
    type DigitalInputBinding,
    type DigitalInputBindingId,
    type InputBindings,
    type ModifierKey,
} from '@sorrem/shared';

/** A binding id turning on or off. */
export type BindingChange =
    /** `isPress` when its own input went down; false when a modifier change made it the one that applies. */
    | { readonly kind: 'start'; readonly id: DigitalInputBindingId; readonly isPress: boolean }
    | { readonly kind: 'end'; readonly id: DigitalInputBindingId };

function inputKey(input: DigitalInput): string {
    switch (input.device) {
        case 'keyboard':
            return `keyboard:${normalizeKey(input.key)}`;
        case 'mouse':
            return `mouse:${input.button}`;
        case 'gamepad':
            return `gamepad:${input.button}`;
    }
}

const ANALOG_IDS: ReadonlySet<string> = new Set(ANALOG_INPUT_BINDING_IDS);

/**
 * Turns held physical inputs into active binding ids. Pure state, no DOM:
 * the capture layer feeds it presses, releases and modifier changes, and it
 * answers with which bindings started or ended.
 *
 * When several bindings share one input (left click, Shift + left click),
 * the one whose modifiers are all held and that requires the most of them
 * wins, so holding Shift turns a left click into an aimed attack.
 */
export class BindingResolver {
    private bindings: readonly (readonly [DigitalInputBindingId, DigitalInputBinding])[] = [];
    private readonly held = new Map<string, DigitalInput>();
    private modifiers: ReadonlySet<ModifierKey> = new Set();
    private active = new Set<DigitalInputBindingId>();

    constructor(bindings: InputBindings) {
        this.setBindings(bindings);
    }

    /** Replaces the binding table. Bindings that no longer apply end; nothing new starts until its input is pressed again. */
    setBindings(bindings: InputBindings): BindingChange[] {
        this.bindings = Object.entries(bindings)
            .filter(([id]) => !ANALOG_IDS.has(id))
            .flatMap(([id, list]) => (list as readonly DigitalInputBinding[])
                .map((binding) => [id as DigitalInputBindingId, binding] as const));
        const stillActive = this.resolveActive();
        const changes: BindingChange[] = [];
        for (const id of this.active) {
            if (!stillActive.has(id)) {
                changes.push({ kind: 'end', id });
            }
        }
        this.active = new Set([...this.active].filter((id) => stillActive.has(id)));
        return changes;
    }

    press(input: DigitalInput): BindingChange[] {
        const key = inputKey(input);
        if (this.held.has(key)) {
            return [];
        }
        this.held.set(key, input);
        return this.update(input);
    }

    release(input: DigitalInput): BindingChange[] {
        if (!this.held.delete(inputKey(input))) {
            return [];
        }
        return this.update(null);
    }

    setModifiers(modifiers: ReadonlySet<ModifierKey>): BindingChange[] {
        if (modifiers.size === this.modifiers.size && [...modifiers].every((modifier) => this.modifiers.has(modifier))) {
            return [];
        }
        this.modifiers = new Set(modifiers);
        return this.update(null);
    }

    /** Lets go of everything, e.g. when focus leaves the page. */
    releaseAll(): BindingChange[] {
        this.held.clear();
        this.modifiers = new Set();
        return this.update(null);
    }

    isActive(id: DigitalInputBindingId): boolean {
        return this.active.has(id);
    }

    /** Whether any binding is on `input` itself, whatever modifiers it needs. */
    isBound(input: DigitalInput): boolean {
        return this.bindings.some(([, binding]) => isSameDigitalInput(binding.input, input));
    }

    private resolveActive(): Set<DigitalInputBindingId> {
        const winners = new Map<string, { id: DigitalInputBindingId; specificity: number }[]>();
        for (const [id, binding] of this.bindings) {
            const key = inputKey(binding.input);
            if (!this.held.has(key)) {
                continue;
            }
            const required = binding.modifiers ?? [];
            if (!required.every((modifier) => this.modifiers.has(modifier))) {
                continue;
            }
            const current = winners.get(key);
            const specificity = required.length;
            if (!current || specificity > current[0]!.specificity) {
                winners.set(key, [{ id, specificity }]);
            } else if (specificity === current[0]!.specificity) {
                current.push({ id, specificity });
            }
        }
        return new Set([...winners.values()].flat().map((winner) => winner.id));
    }

    private update(pressed: DigitalInput | null): BindingChange[] {
        const next = this.resolveActive();
        const changes: BindingChange[] = [];
        for (const id of this.active) {
            if (!next.has(id)) {
                changes.push({ kind: 'end', id });
            }
        }
        for (const id of next) {
            if (this.active.has(id)) {
                continue;
            }
            const isPress = pressed !== null && this.bindings.some(([boundId, binding]) => boundId === id && isSameDigitalInput(binding.input, pressed));
            changes.push({ kind: 'start', id, isPress });
        }
        this.active = next;
        return changes;
    }
}
